/**
 * Pointer drag & drop for tiles — a custom Pointer Events implementation (no
 * DnD library).
 *
 * - A 4px (mouse/pen) / 6px (touch) movement threshold separates a tap from a
 *   drag; a tap is reported via `onTap`.
 * - The pointer is captured on the tile, but move/up/cancel are heard on
 *   `window`: a source that unmounts mid-drag (a board draft tile returned to
 *   the rack by the opponent's move) loses its capture, and listeners on it
 *   would never hear the release.
 * - A "ghost" copy of the tile lives in a body-level layer and follows the
 *   pointer through `transform` only, updated in a requestAnimationFrame
 *   loop — React never re-renders per pointer move.
 * - The hovered board square gets a `data-drop` attribute (CSS draws the
 *   indicator) — again without a React render.
 * - Dropping on a free square places the tile, on the rack reorders/recalls it
 *   at the insertion index, anywhere else glides the ghost back home.
 *
 * The geometry (cell under a point, rack insertion index, threshold) is pure
 * and unit-tested in drag.test.ts.
 */
import { BOARD_SIZE } from '@wordloom/engine';
import type { Coord } from '@wordloom/engine';

// ---------------------------------------------------------------------------
// Pure geometry
// ---------------------------------------------------------------------------

export interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Board square under a viewport point, or null when outside the grid. */
export function cellAtPoint(x: number, y: number, grid: RectLike, size: number = BOARD_SIZE): Coord | null {
  if (grid.width <= 0 || grid.height <= 0) return null;
  const fx = (x - grid.left) / grid.width;
  const fy = (y - grid.top) / grid.height;
  if (fx < 0 || fy < 0 || fx >= 1 || fy >= 1) return null;
  return { row: Math.min(size - 1, Math.floor(fy * size)), col: Math.min(size - 1, Math.floor(fx * size)) };
}

export function pointInRect(x: number, y: number, r: RectLike, pad = 0): boolean {
  return x >= r.left - pad && x <= r.left + r.width + pad && y >= r.top - pad && y <= r.top + r.height + pad;
}

/**
 * Insertion index for a drop on the rack: the number of (other) rack slots
 * whose horizontal centre lies left of the pointer.
 */
export function rackInsertIndex(x: number, slotCenters: readonly number[]): number {
  let index = 0;
  for (const c of slotCenters) if (x > c) index++;
  return index;
}

/** Movement needed before a press becomes a drag. */
export function dragThreshold(pointerType: string): number {
  return pointerType === 'touch' ? 6 : 4;
}

export function exceedsThreshold(dx: number, dy: number, pointerType: string): boolean {
  const t = dragThreshold(pointerType);
  return dx * dx + dy * dy >= t * t;
}

/** Auto-scroll velocity (px/frame) when the pointer nears a scroller edge. */
export function edgeScrollDelta(pos: number, start: number, end: number, edge = 44, maxSpeed = 14): number {
  if (end - start <= edge * 2) return 0;
  if (pos < start + edge) return -Math.ceil(((start + edge - pos) / edge) * maxSpeed);
  if (pos > end - edge) return Math.ceil(((pos - (end - edge)) / edge) * maxSpeed);
  return 0;
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

export type DragOrigin =
  | { kind: 'rack'; tileId: string }
  | { kind: 'board'; tileId: string; row: number; col: number };

export type DropTarget = { kind: 'cell'; row: number; col: number } | { kind: 'rack'; index: number } | { kind: 'none' };

export interface DragHandlers {
  /** Whether the square accepts this tile (free, or the tile's own square). */
  canDropOnCell(origin: DragOrigin, row: number, col: number): boolean;
  onTap(origin: DragOrigin): void;
  onDragStart(origin: DragOrigin): void;
  /** Insertion index while hovering the rack (null when not over it). */
  onRackHover(index: number | null): void;
  /** Return true when the drop was applied; false glides the ghost home. */
  onDrop(origin: DragOrigin, target: DropTarget): boolean;
  onDragEnd(origin: DragOrigin): void;
  reducedMotion(): boolean;
}

interface ActiveDrag {
  origin: DragOrigin;
  source: HTMLElement;
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  x: number;
  y: number;
  dragging: boolean;
  ghost: HTMLDivElement | null;
  ghostInner: HTMLElement | null;
  /** Grab point inside the tile, as a fraction of its size. */
  grabFx: number;
  grabFy: number;
  baseSize: number;
  frame: number;
  hoverCell: HTMLElement | null;
  target: DropTarget;
  /**
   * Edge auto-scroll is armed only once the pointer has been in the scroller's
   * dead zone: a finger coming up from the rack crosses the bottom edge band on
   * every drag, and must not scroll the zoomed board on its way in.
   */
  scrollArmed: boolean;
  cleanup: () => void;
}

class DragController {
  private grid: HTMLElement | null = null;
  private rack: HTMLElement | null = null;
  private scroller: HTMLElement | null = null;
  private handlers: DragHandlers | null = null;
  private active: ActiveDrag | null = null;

  registerGrid(el: HTMLElement | null): void {
    this.grid = el;
  }
  registerRack(el: HTMLElement | null): void {
    this.rack = el;
  }
  registerScroller(el: HTMLElement | null): void {
    this.scroller = el;
  }
  setHandlers(h: DragHandlers | null): void {
    this.handlers = h;
  }
  isDragging(): boolean {
    return this.active?.dragging === true;
  }

  /** Call from a tile's onPointerDown. */
  begin(e: PointerEvent | { nativeEvent: PointerEvent; currentTarget: EventTarget }, origin: DragOrigin): void {
    const ev = 'nativeEvent' in e ? e.nativeEvent : e;
    const source = ('currentTarget' in e ? e.currentTarget : ev.currentTarget) as HTMLElement | null;
    if (!source || !this.handlers || this.active) return;
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;

    try {
      source.setPointerCapture(ev.pointerId);
    } catch {
      /* jsdom / already released */
    }

    const onMove = (m: PointerEvent) => this.move(m);
    const onUp = (u: PointerEvent) => this.end(u, false);
    const onCancel = (c: PointerEvent) => this.end(c, true);
    const onKey = (k: KeyboardEvent) => {
      if (k.key === 'Escape' && this.active?.dragging) {
        k.preventDefault();
        k.stopPropagation();
        this.finish(true);
      }
    };
    // On window, not the source: events for the captured source still bubble
    // here, and a source removed mid-drag no longer receives any (move/end
    // filter on pointerId).
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey, true);

    this.active = {
      origin,
      source,
      pointerId: ev.pointerId,
      pointerType: ev.pointerType || 'mouse',
      startX: ev.clientX,
      startY: ev.clientY,
      x: ev.clientX,
      y: ev.clientY,
      dragging: false,
      ghost: null,
      ghostInner: null,
      grabFx: 0.5,
      grabFy: 0.5,
      baseSize: 0,
      frame: 0,
      hoverCell: null,
      target: { kind: 'none' },
      scrollArmed: false,
      cleanup: () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        window.removeEventListener('keydown', onKey, true);
        try {
          source.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
      },
    };
  }

  private move(e: PointerEvent): void {
    const a = this.active;
    if (!a || e.pointerId !== a.pointerId) return;
    a.x = e.clientX;
    a.y = e.clientY;
    if (!a.dragging) {
      if (!exceedsThreshold(a.x - a.startX, a.y - a.startY, a.pointerType)) return;
      this.startDrag(a);
    }
    e.preventDefault();
  }

  private startDrag(a: ActiveDrag): void {
    a.dragging = true;
    const rect = a.source.getBoundingClientRect();
    a.baseSize = rect.width || 48;
    a.grabFx = rect.width ? (a.startX - rect.left) / rect.width : 0.5;
    a.grabFy = rect.height ? (a.startY - rect.top) / rect.height : 0.5;

    const ghost = document.createElement('div');
    ghost.className = 'drag-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    const inner = a.source.cloneNode(true) as HTMLElement;
    inner.removeAttribute('id');
    inner.removeAttribute('tabindex');
    inner.style.cssText = '';
    inner.classList.add('tile--ghost');
    inner.style.transformOrigin = `${a.grabFx * 100}% ${a.grabFy * 100}%`;
    inner.style.width = '100%';
    inner.style.height = '100%';
    ghost.appendChild(inner);
    document.body.appendChild(ghost);
    a.ghost = ghost;
    a.ghostInner = inner;
    document.documentElement.classList.add('is-dragging');

    this.handlers?.onDragStart(a.origin);
    const loop = () => {
      this.frame();
      if (this.active === a && a.dragging) a.frame = requestAnimationFrame(loop);
    };
    a.frame = requestAnimationFrame(loop);
    this.frame();
  }

  /** One animation frame: move the ghost, hit-test, update indicators, auto-scroll. */
  private frame(): void {
    const a = this.active;
    if (!a?.ghost || !a.ghostInner) return;
    const target = this.hitTest(a.x, a.y, a.origin);
    a.target = target;

    // Ghost size: a board square's size over the board, rack-tile size elsewhere.
    let desired = a.baseSize;
    if (target.kind === 'cell' && this.grid) desired = this.grid.getBoundingClientRect().width / BOARD_SIZE;
    else if (a.origin.kind === 'board') desired = this.rackTileSize() ?? a.baseSize;
    const scale = (desired / a.baseSize) * 1.12;
    const w = a.baseSize;
    a.ghost.style.transform = `translate3d(${a.x - a.grabFx * w}px, ${a.y - a.grabFy * w}px, 0)`;
    const tilt = a.x >= a.startX ? 3 : -3;
    a.ghostInner.style.transform = `scale(${scale.toFixed(3)}) rotate(${tilt}deg)`;

    // Drop indicator on the hovered free square (DOM attribute, no React render).
    const cellEl =
      target.kind === 'cell' ? (this.grid?.querySelector<HTMLElement>(`[data-cell="${target.row},${target.col}"]`) ?? null) : null;
    if (a.hoverCell !== cellEl) {
      if (a.hoverCell) delete a.hoverCell.dataset.drop;
      if (cellEl) cellEl.dataset.drop = 'ok';
      a.hoverCell = cellEl;
    }
    this.handlers?.onRackHover(target.kind === 'rack' ? target.index : null);

    // Auto-scroll a zoomed board when the pointer nears its edges.
    const sc = this.scroller;
    if (sc && sc.scrollWidth > sc.clientWidth + 1) {
      const r = sc.getBoundingClientRect();
      if (pointInRect(a.x, a.y, r)) {
        const dx = edgeScrollDelta(a.x, r.left, r.right);
        const dy = edgeScrollDelta(a.y, r.top, r.bottom);
        if (!dx && !dy) a.scrollArmed = true;
        else if (a.scrollArmed) sc.scrollBy(dx, dy);
      }
    }
  }

  private rackTileSize(): number | null {
    const t = this.rack?.querySelector<HTMLElement>('[data-rack-tile]');
    return t ? t.getBoundingClientRect().width : null;
  }

  private hitTest(x: number, y: number, origin: DragOrigin): DropTarget {
    if (this.grid) {
      const gridRect = this.grid.getBoundingClientRect();
      // When zoomed, only the visible part of the grid counts.
      const visible = this.scroller ? this.scroller.getBoundingClientRect() : gridRect;
      if (pointInRect(x, y, visible)) {
        const cell = cellAtPoint(x, y, gridRect);
        if (cell) {
          return this.handlers?.canDropOnCell(origin, cell.row, cell.col)
            ? { kind: 'cell', row: cell.row, col: cell.col }
            : { kind: 'none' };
        }
      }
    }
    if (this.rack && pointInRect(x, y, this.rack.getBoundingClientRect(), 28)) {
      const slots = Array.from(this.rack.querySelectorAll<HTMLElement>('[data-rack-tile]')).filter(
        (el) => el.dataset.tileId !== origin.tileId,
      );
      const centers = slots.map((el) => {
        const r = el.getBoundingClientRect();
        return r.left + r.width / 2;
      });
      return { kind: 'rack', index: rackInsertIndex(x, centers) };
    }
    return { kind: 'none' };
  }

  private end(e: PointerEvent, cancelled: boolean): void {
    const a = this.active;
    if (!a || e.pointerId !== a.pointerId) return;
    if (!a.dragging) {
      a.cleanup();
      this.active = null;
      if (!cancelled) this.handlers?.onTap(a.origin);
      return;
    }
    a.x = e.clientX || a.x;
    a.y = e.clientY || a.y;
    this.frame();
    this.finish(cancelled);
  }

  private finish(cancelled: boolean): void {
    const a = this.active;
    if (!a) return;
    cancelAnimationFrame(a.frame);
    a.cleanup();
    this.active = null;
    document.documentElement.classList.remove('is-dragging');
    if (a.hoverCell) delete a.hoverCell.dataset.drop;
    this.handlers?.onRackHover(null);

    const accepted = !cancelled && a.target.kind !== 'none' && (this.handlers?.onDrop(a.origin, a.target) ?? false);
    const ghost = a.ghost;
    const reduced = this.handlers?.reducedMotion() ?? false;
    const done = () => {
      ghost?.remove();
      this.handlers?.onDragEnd(a.origin);
    };
    if (!ghost) return done();

    if (accepted || reduced) {
      if (reduced) return done();
      ghost.style.transition = 'opacity 90ms ease-out';
      ghost.style.opacity = '0';
      window.setTimeout(done, 90);
      return;
    }
    // Rejected: glide back to where the tile came from; the source tile (kept
    // invisible in its slot) reappears exactly when the ghost lands.
    const home = a.source.isConnected ? a.source.getBoundingClientRect() : null;
    if (!home || typeof ghost.animate !== 'function') return done();
    const anim = ghost.animate(
      [{ transform: ghost.style.transform }, { transform: `translate3d(${home.left}px, ${home.top}px, 0)` }],
      { duration: 240, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' },
    );
    if (a.ghostInner) {
      a.ghostInner.style.transition = 'transform 240ms cubic-bezier(.2,.8,.2,1)';
      a.ghostInner.style.transform = `scale(${home.width / a.baseSize}) rotate(0deg)`;
    }
    anim.onfinish = done;
    anim.oncancel = done;
  }
}

/** The single drag controller for the game screen. */
export const dragController = new DragController();
